import React, { useState, useMemo, forwardRef, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './Card_link.css';
import {
  Table,
  Form,
  InputGroup,
  Dropdown,
  Nav
} from 'react-bootstrap';
import { list_opportunities } from '../../../../utils/crm_services';

const MODE_STRATEGY = 'strategy';
const MODE_OPPORTUNITY = 'opportunity';
/** Códigos de `card_status` alineados con talent_db.cards.status */
const STATUS_OPTIONS = [
  { code: 0, label: 'Ninguno' },
  { code: 1, label: 'Alineación' },
  { code: 2, label: 'Ejecución' },
  { code: 3, label: 'Terminado' },
  { code: 4, label: 'Archivado' }
];

const Card_link_2task = forwardRef(({
  user_id,
  permission,
  onSet,
  token_num,
  initialRows,
  member_trello_id,
  selectedStrategyID,
  selectedOpportunityID,
  crmPermission,
  token,
  strategyLoading = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'cardname', dir: 'asc' });
  /** Por defecto solo Ejecución (2). Vacío = sin filtrar por estado (mostrar todos). */
  const [selectedStatuses, setSelectedStatuses] = useState(() => [2]);
  const [mode, setMode] = useState(MODE_STRATEGY);
  const [opportunities, setOpportunities] = useState([]);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);

  const canShowOpportunity = Number(crmPermission) > 0;

  useEffect(() => {
    if (mode !== MODE_OPPORTUNITY || !token) return;
    let cancelled = false;
    setOpportunitiesLoading(true);
    list_opportunities(token, '')
      .then((res) => {
        if (cancelled) return;
        if (res && res.opp_data && Array.isArray(res.opp_data)) {
          setOpportunities(res.opp_data);
        } else {
          setOpportunities([]);
        }
      })
      .catch(() => {
        if (!cancelled) setOpportunities([]);
      })
      .finally(() => {
        if (!cancelled) setOpportunitiesLoading(false);
      });
    return () => { cancelled = true; };
  }, [mode, token]);

  const strategyRows = useMemo(() => {
    let result = (initialRows || []).filter((row) => !row.isKr);
    if (selectedStatuses.length > 0) {
      const allowed = new Set(selectedStatuses.map((c) => Number(c)));
      result = result.filter((row) => allowed.has(Number(row.card_status ?? 0)));
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((r) =>
        Object.values(r).some((v) => String(v).toLowerCase().includes(term))
      );
    }
    result = [...result].sort((a, b) => {
      const aVal = String(a[sortConfig.key] ?? '').toLowerCase();
      const bVal = String(b[sortConfig.key] ?? '').toLowerCase();
      if (aVal === bVal) return 0;
      const order = aVal > bVal ? 1 : -1;
      return sortConfig.dir === 'asc' ? order : -order;
    });
    return result;
  }, [searchTerm, sortConfig, initialRows, selectedStatuses]);

  const activeOpportunities = useMemo(() => {
    return (opportunities || []).filter((o) => Number(o.current_status) === 1);
  }, [opportunities]);

  const opportunityRows = useMemo(() => {
    let result = activeOpportunities.map((o) => ({
      id: o.id,
      name: o.name || '',
      channel_name: o.channel_name || ''
    }));
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((r) =>
        String(r.name).toLowerCase().includes(term) ||
        String(r.channel_name).toLowerCase().includes(term)
      );
    }
    return result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [activeOpportunities, searchTerm]);

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key, dir: 'asc' };
    });
  };

  const add_linked_strategy = (id) => {
    onSet(id);
  };

  const add_linked_opportunity = (id) => {
    onSet({ opportunityId: id });
  };

  const unlink_opportunity = () => {
    onSet({ opportunityId: null });
  };

  const toggleStatusCode = (code) => {
    const n = Number(code);
    setSelectedStatuses((prev) => {
      if (prev.includes(n)) {
        return prev.filter((x) => x !== n);
      }
      return [...prev, n].sort((a, b) => a - b);
    });
  };

  const selectOnlyEjecucion = () => setSelectedStatuses([2]);

  const selectAllStatuses = () =>
    setSelectedStatuses(STATUS_OPTIONS.map((o) => o.code));

  const statusFilterSummary = useMemo(() => {
    if (selectedStatuses.length === 0) {
      return 'Sin filtro (todos)';
    }
    if (selectedStatuses.length === STATUS_OPTIONS.length) {
      return 'Todos los estados';
    }
    const sorted = [...selectedStatuses].sort((a, b) => a - b);
    return sorted
      .map((c) => STATUS_OPTIONS.find((o) => o.code === c)?.label ?? String(c))
      .join(', ');
  }, [selectedStatuses]);

  const SortIcon = ({ col }) =>
    sortConfig.key === col ? (sortConfig.dir === 'asc' ? '↑' : '↓') : '↕';

  const panelClassName = canShowOpportunity ? 'card-link-tab-panel' : '';

  return (
    <div className="container py-4">
      {canShowOpportunity && (
        <Nav
          variant="tabs"
          activeKey={mode}
          onSelect={(key) => key && setMode(key)}
          className="card-link-mode-tabs"
        >
          <Nav.Item>
            <Nav.Link eventKey={MODE_STRATEGY}>Vincular tarjeta</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey={MODE_OPPORTUNITY}>Vincular oportunidad</Nav.Link>
          </Nav.Item>
        </Nav>
      )}

      <div className={panelClassName}>
      <div className="d-flex gap-2 mb-3 flex-wrap">
        <InputGroup className="flex-grow-1" style={{ maxWidth: 320 }}>
          <Form.Control
            placeholder={mode === MODE_STRATEGY ? 'Search strategy...' : 'Buscar oportunidad...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="toolbar-control"
          />
        </InputGroup>
        {mode === MODE_STRATEGY && (
          <Dropdown autoClose="outside" align="end">
            <Dropdown.Toggle
              variant="outline-secondary"
              id="card-link-status-filter"
              className="toolbar-control text-start text-truncate"
              style={{ maxWidth: 320, minWidth: 200 }}
            >
              Estado: {statusFilterSummary}
            </Dropdown.Toggle>
            <Dropdown.Menu className="p-0 shadow-sm" style={{ minWidth: 260 }}>
              <div className="px-3 py-2 border-bottom small text-muted">
                Selecciona uno o varios
              </div>
              {STATUS_OPTIONS.map((opt) => (
                <div
                  key={opt.code}
                  className="px-3 py-2"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <Form.Check
                    type="checkbox"
                    id={`card-link-status-${opt.code}`}
                    label={opt.label}
                    checked={selectedStatuses.includes(opt.code)}
                    onChange={() => toggleStatusCode(opt.code)}
                    className="mb-0"
                  />
                </div>
              ))}
              <Dropdown.Divider className="my-0" />
              <Dropdown.Item as="button" type="button" onClick={selectOnlyEjecucion}>
                Solo ejecución
              </Dropdown.Item>
              <Dropdown.Item as="button" type="button" onClick={selectAllStatuses}>
                Marcar todos los estados
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>

      {mode === MODE_STRATEGY && (
        <button
          type="button"
          onClick={() => add_linked_strategy(99999999)}
          className={`card-link-none-option mb-3${!selectedStrategyID || selectedStrategyID === '99999999' || selectedStrategyID === 99999999 ? ' selected-row' : ''}`}
        >
          Ninguna estrategia
        </button>
      )}
      {mode === MODE_OPPORTUNITY && (
        <button
          type="button"
          onClick={unlink_opportunity}
          className="card-link-none-option mb-3"
        >
          Ninguna oportunidad
        </button>
      )}

      <div style={{ height: '400px', overflowY: 'auto' }}>
        {mode === MODE_STRATEGY && (
          <Table hover responsive className="align-middle text-start">
            <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f8f9fa' }}>
              <tr>
                <th onClick={() => toggleSort('cardname')} role="button">
                  Strategies <SortIcon col="cardname" />
                </th>
                <th onClick={() => toggleSort('estado')} role="button" style={{ width: 180 }}>
                  Estado <SortIcon col="estado" />
                </th>
              </tr>
            </thead>
            <tbody>
              {strategyRows.map((row) => (
                <tr
                  key={row.cardid}
                  onClick={() => add_linked_strategy(row.cardid)}
                  className={`clickable-row ${row.cardid === selectedStrategyID ? 'selected-row' : ''}${row.private ? ' card-link-private-row' : ''}`}
                >
                  <td>
                    {row.private ? (
                      <span className="badge text-bg-light border me-2" style={{ color: '#7c3aed', borderColor: '#c4b5fd' }}>
                        Privada
                      </span>
                    ) : null}
                    {row.cardname}
                  </td>
                  <td>{row.estado || 'Ninguno'}</td>
                </tr>
              ))}
              {strategyLoading && (
                <tr>
                  <td colSpan="10" className="text-center py-4 text-start">
                    Cargando...
                  </td>
                </tr>
              )}
              {!strategyLoading && (!initialRows || initialRows.length === 0) && (
                <tr>
                  <td colSpan="10" className="text-center py-4 text-start">
                    No hay tarjetas disponibles
                  </td>
                </tr>
              )}
              {!strategyLoading && initialRows && initialRows.length !== 0 && strategyRows.length === 0 && (
                <tr>
                  <td colSpan="10" className="text-center py-4 text-start">
                    No results
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}

        {mode === MODE_OPPORTUNITY && (
          <>
            {opportunitiesLoading ? (
              <div className="text-center py-4">Cargando oportunidades...</div>
            ) : (
              <Table hover responsive className="align-middle text-start">
                <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th>Oportunidades (activas)</th>
                  </tr>
                </thead>
                <tbody>
                  {opportunityRows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => add_linked_opportunity(row.id)}
                      className={`clickable-row ${String(row.id) === String(selectedOpportunityID) ? 'selected-row' : ''}`}
                    >
                      <td>{row.name || `Oportunidad #${row.id}`}</td>
                    </tr>
                  ))}
                  {opportunityRows.length === 0 && !opportunitiesLoading && (
                    <tr>
                      <td colSpan="10" className="text-center py-4 text-start">
                        No hay oportunidades activas
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </>
        )}
      </div>
      </div>
    </div>
  );
});

Card_link_2task.displayName = 'Card_link_2task';

export default Card_link_2task;
